// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useCallback, useEffect, useId } from 'react';
import { useDash } from '../context';
import { useWebQuery, type WebQueryResult } from '../useWebQuery';
import type { BreakdownResponse, BreakdownRow, FunnelResponse, SummaryResponse, TimeseriesResponse, WebDim } from '../types';
import type { BarRow } from '../charts/BarList';
import { BarList } from '../charts/BarList';
import { Donut } from '../charts/Donut';
import { Card, ErrorLine, MoreButton } from '../ui';
import { valueLabel, type MetricId } from '../format';

/** Report a query's loading state to the dashboard-wide gem overlay. */
export function useTrack<T>(q: WebQueryResult<T>): WebQueryResult<T> {
  const { reportLoading } = useDash();
  const id = useId();
  useEffect(() => { reportLoading(id, q.loading); }, [id, q.loading, reportLoading]);
  useEffect(() => () => reportLoading(id, false), [id, reportLoading]);
  return q;
}

/** Rollup breakdown, or POST /query { breakdown } when filters are active. */
export function useBreakdown(dim: WebDim, limit = 10, enabled = true): WebQueryResult<BreakdownResponse> {
  const d = useDash();
  return useTrack(useWebQuery<BreakdownResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: d.state.filters,
    want: { breakdown: dim }, limit, enabled,
    unfiltered: () => d.api.breakdown(d.siteId, d.state.range, dim, limit),
  }));
}

export function useSummary(): WebQueryResult<SummaryResponse> {
  const d = useDash();
  return useTrack(useWebQuery<SummaryResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: d.state.filters, want: 'summary',
    unfiltered: () => d.api.summary(d.siteId, d.state.range),
  }));
}

export function useTimeseries(metric: MetricId): WebQueryResult<TimeseriesResponse> {
  const d = useDash();
  return useTrack(useWebQuery<TimeseriesResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: d.state.filters, want: 'timeseries', metric,
    unfiltered: () => d.api.timeseries(d.siteId, d.state.range, metric),
  }));
}

export function useFunnel(): WebQueryResult<FunnelResponse> {
  const d = useDash();
  return useTrack(useWebQuery<FunnelResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: d.state.filters, want: 'funnel',
    unfiltered: () => d.api.funnel(d.siteId, d.state.range),
  }));
}

/** Breakdown rows → BarList rows; each row click adds a filter chip. */
export function useRows() {
  const d = useDash();
  return useCallback((dim: WebDim, rows: BreakdownRow[] | undefined, value: (r: BreakdownRow) => number = (r) => r.visits): BarRow[] =>
    (rows || []).map((r) => ({
      key: r.value,
      label: valueLabel(dim, r.value),
      title: r.value,
      value: value(r),
      onClick: d.rowFilter(dim, r.value),
    })), [d]);
}

/** A card holding one breakdown as a bar list, with an optional "View all →". */
export const BreakdownCard: React.FC<{
  title: React.ReactNode; dim: WebDim; limit?: number; value?: (r: BreakdownRow) => number; format?: (v: number) => string;
  viewAll?: boolean; donut?: boolean; testId?: string; note?: React.ReactNode;
}> = ({ title, dim, limit = 6, value, format, viewAll, donut, testId, note }) => {
  const d = useDash();
  const q = useBreakdown(dim, Math.max(limit, 10));
  const toRows = useRows();
  const rows = toRows(dim, q.data?.rows, value).filter((r) => !donut || r.value > 0);
  return (
    <Card
      title={title} testId={testId}
      action={viewAll ? <MoreButton onClick={() => d.openViewAll(dim, typeof title === 'string' ? title : dim)}>View all →</MoreButton> : undefined}
    >
      {q.error ? <ErrorLine message={q.error} /> : donut ? <Donut parts={rows} /> : <BarList rows={rows} limit={limit} format={format} />}
      {note}
    </Card>
  );
};
